import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { Check, ArrowRight, ClipboardList, Video, Music, UserCheck, CreditCard, ListChecks } from 'lucide-react'
import { AppSignupLink } from '@/components/AppSignupLink'
import { capability } from '@gjl/voice/pricing'

const applications = capability('applications')!

const features = [
  {
    icon: Music,
    title: 'Repertoire as Structured Data',
    description:
      'Composer, work, and movement are separate fields, not one line of free text. Reviewers can sort a pool by what people are actually playing.',
  },
  {
    icon: Video,
    title: 'Per-Movement Audition Video',
    description:
      'Applicants upload video per piece or per movement. Reviewers watch inside the review screen instead of downloading a folder of files.',
  },
  {
    icon: UserCheck,
    title: 'Recommendations, Tracked',
    description:
      'Teacher and conductor recommendations are requested directly, arrive separately from the application, and show their status without anyone chasing email.',
  },
  {
    icon: CreditCard,
    title: 'Fees and Waivers',
    description:
      'Collect the application fee on your own site through Stripe, and grant waivers without a side channel or a refund.',
  },
  {
    icon: ListChecks,
    title: 'Faculty Review Workflow',
    description:
      'Assign reviewers to pools, score against your own rubric, and leave notes that stay attached to the applicant.',
  },
  {
    icon: ClipboardList,
    title: 'Decisions and Seasons',
    description:
      'Accept, waitlist, and decline with the emails attached to the decision. Prior seasons stay searchable next to the current cycle.',
  },
]

const programTypes = [
  {
    title: 'Summer Festivals',
    description:
      'Instrumental and vocal intake with repertoire, references, and per-instrument routing across multiple programs in one cycle.',
  },
  {
    title: 'Young Artist Programs',
    description:
      'Audition rounds, per-aria video, and faculty panels, with the listing sites you already use pointing at your own application.',
  },
  {
    title: 'Pre-College and Summer Academies',
    description:
      'Parent and student contact handled separately, teacher recommendations, and fee waivers as a first-class case rather than an exception.',
  },
  {
    title: 'Competitions',
    description: 'Rounds, blind review where you want it, and per-round scoring that adds up without a spreadsheet.',
  },
]

const otherFeatures = [
  { title: 'Website', href: '/platform/features/website', description: 'Build your site' },
  { title: 'Ticketing', href: '/platform/features/events', description: 'Manage concerts' },
  { title: 'Email', href: '/platform/features/email', description: 'Reach your audience' },
  { title: 'Analytics', href: '/platform/features/analytics', description: 'Track growth' },
]

export default function ApplicationsFeaturePage() {
  useSEO({
    title: 'Application and Audition Management | Fugue Lab App',
    description:
      'Music-native application and audition management in the Fugue Lab app: structured repertoire, per-movement audition video, tracked recommendations, fees, and faculty review workflow.',
    url: '/platform/features/applications',
    canonical: '/platform/features/applications',
  })

  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const featuresRef = useStaggeredGrid<HTMLDivElement>('.feature-card', { stagger: 0.1 })
  const programTypesRef = useStaggeredGrid<HTMLDivElement>('.program-type-card', { stagger: 0.1 })
  const otherFeaturesRef = useStaggeredGrid<HTMLDivElement>('.other-feature-card', { stagger: 0.1 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="px-4 py-20">
        <div ref={heroRef} className="container mx-auto max-w-4xl text-center">
          <div className="bg-primary/10 text-primary mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium">
            <ClipboardList className="h-4 w-4" />
            Applications & Auditions
          </div>
          <h1 className="mb-6 text-4xl font-bold md:text-5xl">
            An application form
            <br />
            that speaks music
          </h1>
          <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-xl">
            Repertoire, audition video, and recommendations described the way your program actually describes them,
            running on your own website instead of a vendor’s marketplace.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <AppSignupLink>
              <m.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button size="lg" className="cursor-pointer px-8 text-lg">
                  Start Your Application Cycle <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </m.div>
            </AppSignupLink>
            <Link to="/services/applications">
              <m.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="outline" size="lg" className="cursor-pointer px-8 text-lg">
                  See Pricing & Setup
                </Button>
              </m.div>
            </Link>
          </div>
          <p className="text-muted-foreground mt-4 text-sm">
            ${applications.priceMonthly}/month, flat. No per-applicant fees.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="bg-surface px-4 py-20">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Built for how music programs actually recruit</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl">
              General admissions tools store an application. These fields let you read one.
            </p>
          </div>
          <div ref={featuresRef} className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => (
              <m.div
                key={feature.title}
                className="feature-card border-border bg-card rounded-lg border p-6"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <feature.icon className="text-primary mb-4 h-10 w-10" />
                <h3 className="mb-2 text-xl font-semibold">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      {/* Program types */}
      <section className="px-4 py-20">
        <div className="container mx-auto max-w-5xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Programs running on it</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl">
              The same field model covers instrumental, vocal, conducting, and composition intake.
            </p>
          </div>
          <div ref={programTypesRef} className="grid gap-6 md:grid-cols-2">
            {programTypes.map((type) => (
              <m.div
                key={type.title}
                className="program-type-card border-border bg-card rounded-lg border p-6"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <h3 className="mb-2 text-xl font-semibold">{type.title}</h3>
                <p className="text-muted-foreground">{type.description}</p>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      {/* One record */}
      <section className="bg-surface px-4 py-20">
        <div className="container mx-auto max-w-3xl space-y-6 text-center">
          <h2 className="text-3xl font-bold">One record, from applicant to alum</h2>
          <p className="text-muted-foreground text-lg">
            An applicant who attends becomes a participant, then an alum, then a donor, on the same contact record. No
            re-import, no duplicate, and no separate list to reconcile before the year-end appeal.
          </p>
          <ul className="mx-auto max-w-md space-y-3 text-left">
            {[
              'Applications share the audience record with email and giving',
              'Alumni segments build themselves from past cycles',
              'Applicant data exports cleanly if you ever leave',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-success" />
                <span className="text-muted-foreground">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Other features */}
      <section className="px-4 py-20">
        <div className="container mx-auto max-w-5xl">
          <h2 className="mb-12 text-center text-3xl font-bold">Explore Other Features</h2>
          <div ref={otherFeaturesRef} className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {otherFeatures.map((feature) => (
              <m.div
                key={feature.title}
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <Link
                  to={feature.href}
                  className="other-feature-card border-border bg-card hover:border-primary/40 block rounded-lg border p-6 transition"
                >
                  <h3 className="font-semibold">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </Link>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary/5 px-4 py-16">
        <div ref={ctaRef} className="container mx-auto max-w-3xl text-center">
          <div className="cta-content">
            <h2 className="mb-4 text-3xl font-bold">Ready to open applications?</h2>
            <p className="text-muted-foreground mb-8 text-xl">
              Bring your current form and your open date. You get a straight answer about what the same cycle looks like
              here.
            </p>
            <a href="/book" target="_blank" rel="noopener noreferrer">
              <m.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                className="inline-block"
              >
                <Button size="lg" className="cursor-pointer px-8 text-lg">
                  Book a Free Call <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </m.div>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
